import api, { BASE_DOMAIN, ensureCsrfCookie } from "../axiosConfig";

const resolveApiUrl = (value) => {
  if (!value) {
    return "";
  }

  try {
    const base = new URL(BASE_DOMAIN);
    const resolved = new URL(value, BASE_DOMAIN);

    if (resolved.origin === base.origin) {
      return `${resolved.pathname}${resolved.search}`;
    }

    return resolved.toString();
  } catch (error) {
    return value;
  }
};

const listConversations = async (channel, params = {}) => {
  await ensureCsrfCookie();

  const safeParams = {
    q: params.q || undefined,
    status: params.status || undefined,
    assigned: params.assigned || undefined,
    channel: params.channel || channel || undefined,
    priority: params.priority || undefined,
    workstream: params.workstream || undefined,
    whatsapp_line_scope: params.whatsapp_line_scope || params.whatsappLineScope || undefined,
    whatsapp_line_id: params.whatsapp_line_id || params.whatsappLineId || undefined,
    created_window: params.created_window || params.createdWindow || undefined,
    closed_window: params.closed_window || params.closedWindow || undefined,
    sort_by: params.sort_by || params.sortBy || undefined,
    sort_direction: params.sort_direction || params.sortDirection || undefined,
    page: params.page || undefined,
    per_page: params.per_page || params.perPage || 25,
  };

  const path = channel ? `/omnichannel/${channel}/conversations` : "/omnichannel/conversations";
  const res = await api.get(path, { params: safeParams });
  return res.data;
};

const getConversationSummary = async (params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/conversations/summary", {
    params: {
      channel: params.channel || undefined,
      whatsapp_line_scope: params.whatsapp_line_scope || params.whatsappLineScope || undefined,
      whatsapp_line_id: params.whatsapp_line_id || params.whatsappLineId || undefined,
    },
  });

  return res.data;
};

const listRecentDrafts = async (params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/drafts/recent", {
    params: {
      limit: params.limit || undefined,
    },
  });

  return res.data;
};

const getConversation = async (channel, conversationId) => {
  await ensureCsrfCookie();

  const path = channel
    ? `/omnichannel/${channel}/conversations/${conversationId}`
    : `/omnichannel/conversations/${conversationId}`;
  const res = await api.get(path);
  return res.data;
};

/**
 * Fetch a paginated slice of messages for a conversation.
 *
 * @param {string|number} conversationId
 * @param {{ before_id?: number, limit?: number }} params
 *   before_id — cursor: return messages older than this ID
 *   limit     — how many to return (default 40 on the server, max 100)
 *
 * @returns {{ messages: object[], has_more: boolean, oldest_id: number|null }}
 */
const getConversationMessages = async (conversationId, params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get(`/omnichannel/conversations/${conversationId}/messages`, {
    params: {
      before_id: params.before_id || undefined,
      limit: params.limit || undefined,
    },
  });
  return res.data;
};

const listQuoteQueue = async (params = {}) => {
  await ensureCsrfCookie();

  const safeParams = {
    status: params.status || undefined,
    assigned: params.assigned || undefined,
    owner_id: params.owner_id || params.ownerId || undefined,
    hotness_band: params.hotness_band || params.hotnessBand || undefined,
    service_type: params.service_type || params.serviceType || undefined,
    channel: params.channel || undefined,
    whatsapp_line_scope: params.whatsapp_line_scope || params.whatsappLineScope || undefined,
    whatsapp_line_id: params.whatsapp_line_id || params.whatsappLineId || undefined,
    workstream_id: params.workstream_id || params.workstreamId || undefined,
    limit: params.limit || undefined,
  };

  const res = await api.get("/omnichannel/quotes", { params: safeParams });
  return res.data;
};

const getQuoteOverview = async (params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/quotes/overview", {
    params: {
      channel: params.channel || undefined,
      whatsapp_line_scope: params.whatsapp_line_scope || params.whatsappLineScope || undefined,
      whatsapp_line_id: params.whatsapp_line_id || params.whatsappLineId || undefined,
      workstream_id: params.workstream_id || params.workstreamId || undefined,
    },
  });
  return res.data;
};

const convertConversationToQuote = async (conversationId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/quotes/convert`, payload);
  return res.data;
};

const confirmConversationQuote = async (conversationId) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/quotes/confirm`);
  return res.data;
};

const saveQuoteVersion = async (quoteId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/quotes/${quoteId}/versions`, payload);
  return res.data;
};

const markQuoteSent = async (quoteId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/quotes/${quoteId}/mark-sent`, payload);
  return res.data;
};

const updateQuoteStatus = async (quoteId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/quotes/${quoteId}/status`, payload);
  return res.data;
};

const mergeQuotes = async (quoteId, sourceQuoteId) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/quotes/${quoteId}/merge`, { source_quote_id: sourceQuoteId });
  return res.data;
};

const startQuoteIntervention = async (quoteId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/quotes/${quoteId}/intervene`, payload);
  return res.data;
};

const resolveQuoteIntervention = async (quoteId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/quotes/${quoteId}/intervention/resolve`, payload);
  return res.data;
};

const linkConversationToInquiry = async (conversationId, inquiryId) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/link-inquiry`, {
    inquiry_id: inquiryId,
  });

  return res.data;
};

const assignConversation = async (conversationId, assignedTo, syncInquiry = true) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/assign`, {
    assigned_to: assignedTo,
    sync_inquiry: syncInquiry,
  });

  return res.data;
};

const createInquiryFromConversation = async (conversationId) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/create-inquiry`);
  return res.data;
};

const startConversation = async (payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post("/omnichannel/conversations/start", payload);
  return res.data;
};

const sendConversationReply = async (conversationId, payload = {}) => {
  await ensureCsrfCookie();

  const attachments = Array.isArray(payload.attachments) ? payload.attachments.filter(Boolean) : [];
  const body = payload.body ?? "";

  const res = attachments.length
    ? await (() => {
        const formData = new FormData();
        formData.append("body", body);

        if (payload.subject) {
          formData.append("subject", payload.subject);
        }

        if (payload.html) {
          formData.append("html", payload.html);
        }

        if (payload.manager_override) {
          formData.append("manager_override", "1");
        }

        attachments.forEach((attachment) => {
          formData.append("attachments[]", attachment);
        });

        return api.post(`/omnichannel/conversations/${conversationId}/reply`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      })()
    : await api.post(`/omnichannel/conversations/${conversationId}/reply`, {
        body,
        subject: payload.subject,
        html: payload.html,
        manager_override: payload.manager_override || false,
      });

  return res.data;
};

const fetchConversationMessageMedia = async (conversationId, messageId) => {
  await ensureCsrfCookie();

  const res = await api.get(`/omnichannel/conversations/${conversationId}/messages/${messageId}/media`, {
    responseType: "blob",
  });

  return res.data;
};

const fetchProtectedAttachment = async (url) => {
  await ensureCsrfCookie();

  const res = await api.get(resolveApiUrl(url), {
    baseURL: BASE_DOMAIN,
    responseType: "blob",
    headers: {
      Accept: "application/octet-stream",
    },
  });

  return {
    blob: res.data,
    contentDisposition: res.headers?.["content-disposition"] || "",
    contentType: res.headers?.["content-type"] || res.data?.type || "",
  };
};

const conversationMessageAttachmentUrl = (conversationId, messageId, attachmentId, options = {}) => {
  const params = new URLSearchParams();
  if (options.download) {
    params.set("download", "1");
  }

  const query = params.toString();

  return `${BASE_DOMAIN}/api/omnichannel/conversations/${conversationId}/messages/${messageId}/attachments/${attachmentId}${query ? `?${query}` : ""}`;
};

const createInternalNote = async (conversationId, body) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/notes`, { body });
  return res.data;
};

const claimConversation = async (conversationId, claim = true) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/claim`, { claim });
  return res.data;
};

const sendHeartbeat = async (conversationId) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/heartbeat`);
  return res.data;
};

const getConversationPresence = async (conversationId) => {
  await ensureCsrfCookie();

  const res = await api.get(`/omnichannel/conversations/${conversationId}/presence`);
  return res.data;
};

const acquireConversationLock = async (conversationId, force = false) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/lock`, { force });
  return res.data;
};

const releaseConversationLock = async (conversationId, force = false) => {
  await ensureCsrfCookie();

  const res = await api.delete(`/omnichannel/conversations/${conversationId}/lock`, {
    data: { force },
  });
  return res.data;
};

const updateConversationStatus = async (conversationId, status) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/status`, { status });
  return res.data;
};

const updateConversationPriority = async (conversationId, priority) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/priority`, { priority });
  return res.data;
};

const saveFollowUp = async (conversationId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/follow-ups`, payload);
  return res.data;
};

const logVoiceCall = async (conversationId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/voice-calls`, payload);
  return res.data;
};

const listTelephonyOptions = async () => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/telephony-options");
  return res.data;
};

const initiateVoiceCall = async (conversationId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/conversations/${conversationId}/voice-calls/initiate`, payload);
  return res.data;
};

const getMyAvailability = async () => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/availability/me");
  return res.data;
};

const updateMyAvailability = async (payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.put("/omnichannel/availability/me", payload);
  return res.data;
};

const listCannedReplies = async (params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/canned-replies", {
    params: {
      channel: params.channel || undefined,
      workstream_id: params.workstream_id || undefined,
      q: params.q || undefined,
    },
  });

  return res.data;
};

const createCannedReply = async (payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post("/omnichannel/canned-replies", payload);
  return res.data;
};

const archiveCannedReply = async (cannedReplyId) => {
  await ensureCsrfCookie();

  const res = await api.delete(`/omnichannel/canned-replies/${cannedReplyId}`);
  return res.data;
};

const listWorkstreams = async () => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/workstreams");
  return res.data;
};

const getGmailInboxStatus = async () => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/email/google/status");
  return res.data?.data || null;
};

const getContactTimeline = async (contactId) => {
  await ensureCsrfCookie();

  const res = await api.get(`/omnichannel/contacts/${contactId}/conversations`);
  return res.data;
};

const setHotClientStatus = async (contactId, { is_hot_client, hot_client_note = null }) => {
  await ensureCsrfCookie();
  const res = await api.post(`/omnichannel/contacts/${contactId}/hot-client`, {
    is_hot_client,
    hot_client_note: hot_client_note || null,
  });
  return res.data;
};

const analyzeConversation = async (conversationId) => {
  await ensureCsrfCookie();
  const res = await api.post(`/omnichannel/conversations/${conversationId}/analyze`);
  return res.data;
};

const syncGmailInboxNow = async () => {
  await ensureCsrfCookie();

  const res = await api.post("/users/me/mailbox/sync-now");
  return res.data;
};

const updateWorkstream = async (workstreamId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.patch(`/omnichannel/workstreams/${workstreamId}`, payload);
  return res.data;
};

const getWorkstreamPerformance = async (workstreamId, params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get(`/omnichannel/workstreams/${workstreamId}/performance`, {
    params: {
      days: params.days || undefined,
    },
  });
  return res.data;
};

const saveWorkstreamMember = async (workstreamId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/workstreams/${workstreamId}/members`, payload);
  return res.data;
};

const updateWorkstreamMember = async (workstreamId, userId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.patch(`/omnichannel/workstreams/${workstreamId}/members/${userId}`, payload);
  return res.data;
};

const listDutyRosters = async (workstreamId) => {
  await ensureCsrfCookie();

  const res = await api.get(`/omnichannel/workstreams/${workstreamId}/duty-rosters`);
  return res.data;
};

const saveDutyRoster = async (workstreamId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/workstreams/${workstreamId}/duty-rosters`, payload);
  return res.data;
};

const createDutyRosterAssignment = async (dutyRosterId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/duty-rosters/${dutyRosterId}/assignments`, payload);
  return res.data;
};

const listSlaRules = async (params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/sla-rules", {
    params: {
      workstream_id: params.workstream_id || undefined,
    },
  });
  return res.data;
};

const saveSlaRule = async (payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post("/omnichannel/sla-rules", payload);
  return res.data;
};

const listIdentityConflicts = async (params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/identity-conflicts", {
    params: {
      workstream_id: params.workstream_id || undefined,
      limit: params.limit || undefined,
    },
  });
  return res.data;
};

const resolveIdentityConflict = async (identityId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/identity-conflicts/${identityId}/resolve`, payload);
  return res.data;
};

const listPossibleIdentityMatches = async (params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/identity-possible-matches", {
    params: {
      workstream_id: params.workstream_id || undefined,
      status: params.status || undefined,
      limit: params.limit || undefined,
    },
  });
  return res.data;
};

const reviewPossibleIdentityMatch = async (matchId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/identity-possible-matches/${matchId}/review`, payload);
  return res.data;
};

const mergeContacts = async (payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post("/omnichannel/contacts/merge", payload);
  return res.data;
};

const getProviderHealth = async (params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/provider-health", {
    params: {
      workstream_id: params.workstream_id || undefined,
    },
  });
  return res.data;
};

const getReadiness = async (params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/readiness", {
    params: {
      workstream_id: params.workstream_id || undefined,
    },
  });
  return res.data;
};

const listTelephonyAccounts = async () => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/telephony-accounts");
  return res.data;
};

const saveTelephonyAccount = async (payload = {}) => {
  await ensureCsrfCookie();

  const method = payload.id ? "patch" : "post";
  const path = payload.id
    ? `/omnichannel/telephony-accounts/${payload.id}`
    : "/omnichannel/telephony-accounts";
  const res = await api[method](path, payload);
  return res.data;
};

const listEventLogs = async (params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/event-logs", {
    params: {
      workstream_id: params.workstream_id || undefined,
      provider: params.provider || undefined,
      status: params.status || undefined,
      source: params.source || undefined,
      channel: params.channel || undefined,
      limit: params.limit || undefined,
    },
  });
  return res.data;
};

const replayEventLog = async (eventLogId) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/event-logs/${eventLogId}/replay`);
  return res.data;
};

const retryEventLogSend = async (eventLogId) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/event-logs/${eventLogId}/retry-send`);
  return res.data;
};

const listAuditLogs = async (params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/audit-logs", {
    params: {
      workstream_id: params.workstream_id || undefined,
      event: params.event || undefined,
      batch_uuid: params.batch_uuid || undefined,
      conversation_id: params.conversation_id || undefined,
      limit: params.limit || undefined,
    },
  });
  return res.data;
};

const listEscalations = async (params = {}) => {
  await ensureCsrfCookie();

  const res = await api.get("/omnichannel/escalations", {
    params: {
      workstream_id: params.workstream_id || undefined,
      status: params.status || undefined,
      rule_code: params.rule_code || undefined,
      limit: params.limit || undefined,
    },
  });
  return res.data;
};

const scanEscalations = async (payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post("/omnichannel/escalations/scan", payload);
  return res.data;
};

const resolveEscalation = async (escalationId) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/escalations/${escalationId}/resolve`);
  return res.data;
};

const retryEscalation = async (escalationId, payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post(`/omnichannel/escalations/${escalationId}/retry`, payload);
  return res.data;
};

const bulkConversationAction = async (payload = {}) => {
  await ensureCsrfCookie();

  const res = await api.post("/omnichannel/conversations/bulk", payload);
  return res.data;
};

export const omnichannelInboxApi = {
  listConversations(params = {}) {
    return listConversations("", params);
  },

  getConversationSummary(params = {}) {
    return getConversationSummary(params);
  },

  listRecentDrafts(params = {}) {
    return listRecentDrafts(params);
  },

  getConversation(conversationId) {
    return getConversation("", conversationId);
  },

  getConversationMessages(conversationId, params = {}) {
    return getConversationMessages(conversationId, params);
  },

  listQuoteQueue(params = {}) {
    return listQuoteQueue(params);
  },

  getQuoteOverview(params = {}) {
    return getQuoteOverview(params);
  },

  convertConversationToQuote(conversationId, payload = {}) {
    return convertConversationToQuote(conversationId, payload);
  },

  confirmConversationQuote(conversationId) {
    return confirmConversationQuote(conversationId);
  },

  saveQuoteVersion(quoteId, payload = {}) {
    return saveQuoteVersion(quoteId, payload);
  },

  markQuoteSent(quoteId, payload = {}) {
    return markQuoteSent(quoteId, payload);
  },

  updateQuoteStatus(quoteId, payload = {}) {
    return updateQuoteStatus(quoteId, payload);
  },

  mergeQuotes(quoteId, sourceQuoteId) {
    return mergeQuotes(quoteId, sourceQuoteId);
  },

  startQuoteIntervention(quoteId, payload = {}) {
    return startQuoteIntervention(quoteId, payload);
  },

  resolveQuoteIntervention(quoteId, payload = {}) {
    return resolveQuoteIntervention(quoteId, payload);
  },

  listWhatsAppConversations(params = {}) {
    return listConversations("whatsapp", params);
  },

  getWhatsAppConversation(conversationId) {
    return getConversation("whatsapp", conversationId);
  },

  listEmailConversations(params = {}) {
    return listConversations("email", params);
  },

  getEmailConversation(conversationId) {
    return getConversation("email", conversationId);
  },

  searchInquiries(params = {}) {
    return ensureCsrfCookie().then(() =>
      api.get("/omnichannel/inquiries/search", {
        params: {
          q: params.q || "",
          limit: params.limit || 10,
        },
      }).then((res) => res.data)
    );
  },

  listAssignableUsers(params = {}) {
    return ensureCsrfCookie().then(() =>
      api.get("/omnichannel/assignable-users", {
        params: {
          workstream_id: params.workstream_id || undefined,
        },
      }).then((res) => res.data)
    );
  },

  linkConversationToInquiry(conversationId, inquiryId) {
    return linkConversationToInquiry(conversationId, inquiryId);
  },

  assignConversation(conversationId, assignedTo, syncInquiry = true) {
    return assignConversation(conversationId, assignedTo, syncInquiry);
  },

  createInquiryFromConversation(conversationId) {
    return createInquiryFromConversation(conversationId);
  },

  startConversation(payload = {}) {
    return startConversation(payload);
  },

  sendConversationReply(conversationId, payload = {}) {
    return sendConversationReply(conversationId, payload);
  },

  fetchConversationMessageMedia(conversationId, messageId) {
    return fetchConversationMessageMedia(conversationId, messageId);
  },

  fetchProtectedAttachment(url) {
    return fetchProtectedAttachment(url);
  },

  conversationMessageAttachmentUrl(conversationId, messageId, attachmentId, options = {}) {
    return conversationMessageAttachmentUrl(conversationId, messageId, attachmentId, options);
  },

  createInternalNote(conversationId, body) {
    return createInternalNote(conversationId, body);
  },

  claimConversation(conversationId, claim = true) {
    return claimConversation(conversationId, claim);
  },

  sendHeartbeat(conversationId) {
    return sendHeartbeat(conversationId);
  },

  getConversationPresence(conversationId) {
    return getConversationPresence(conversationId);
  },

  acquireConversationLock(conversationId, force = false) {
    return acquireConversationLock(conversationId, force);
  },

  releaseConversationLock(conversationId, force = false) {
    return releaseConversationLock(conversationId, force);
  },

  updateConversationStatus(conversationId, status) {
    return updateConversationStatus(conversationId, status);
  },

  updateConversationPriority(conversationId, priority) {
    return updateConversationPriority(conversationId, priority);
  },

  saveFollowUp(conversationId, payload = {}) {
    return saveFollowUp(conversationId, payload);
  },

  logVoiceCall(conversationId, payload = {}) {
    return logVoiceCall(conversationId, payload);
  },

  listTelephonyOptions() {
    return listTelephonyOptions();
  },

  initiateVoiceCall(conversationId, payload = {}) {
    return initiateVoiceCall(conversationId, payload);
  },

  getMyAvailability() {
    return getMyAvailability();
  },

  updateMyAvailability(payload = {}) {
    return updateMyAvailability(payload);
  },

  listCannedReplies(params = {}) {
    return listCannedReplies(params);
  },

  createCannedReply(payload = {}) {
    return createCannedReply(payload);
  },

  archiveCannedReply(cannedReplyId) {
    return archiveCannedReply(cannedReplyId);
  },

  listWorkstreams() {
    return listWorkstreams();
  },

  getGmailInboxStatus() {
    return getGmailInboxStatus();
  },

  syncGmailInboxNow() {
    return syncGmailInboxNow();
  },

  updateWorkstream(workstreamId, payload = {}) {
    return updateWorkstream(workstreamId, payload);
  },

  getWorkstreamPerformance(workstreamId, params = {}) {
    return getWorkstreamPerformance(workstreamId, params);
  },

  saveWorkstreamMember(workstreamId, payload = {}) {
    return saveWorkstreamMember(workstreamId, payload);
  },

  updateWorkstreamMember(workstreamId, userId, payload = {}) {
    return updateWorkstreamMember(workstreamId, userId, payload);
  },

  listDutyRosters(workstreamId) {
    return listDutyRosters(workstreamId);
  },

  saveDutyRoster(workstreamId, payload = {}) {
    return saveDutyRoster(workstreamId, payload);
  },

  createDutyRosterAssignment(dutyRosterId, payload = {}) {
    return createDutyRosterAssignment(dutyRosterId, payload);
  },

  listSlaRules(params = {}) {
    return listSlaRules(params);
  },

  saveSlaRule(payload = {}) {
    return saveSlaRule(payload);
  },

  listIdentityConflicts(params = {}) {
    return listIdentityConflicts(params);
  },

  resolveIdentityConflict(identityId, payload = {}) {
    return resolveIdentityConflict(identityId, payload);
  },

  listPossibleIdentityMatches(params = {}) {
    return listPossibleIdentityMatches(params);
  },

  reviewPossibleIdentityMatch(matchId, payload = {}) {
    return reviewPossibleIdentityMatch(matchId, payload);
  },

  mergeContacts(payload = {}) {
    return mergeContacts(payload);
  },

  getProviderHealth(params = {}) {
    return getProviderHealth(params);
  },

  getReadiness(params = {}) {
    return getReadiness(params);
  },

  listTelephonyAccounts() {
    return listTelephonyAccounts();
  },

  saveTelephonyAccount(payload = {}) {
    return saveTelephonyAccount(payload);
  },

  listEventLogs(params = {}) {
    return listEventLogs(params);
  },

  replayEventLog(eventLogId) {
    return replayEventLog(eventLogId);
  },

  retryEventLogSend(eventLogId) {
    return retryEventLogSend(eventLogId);
  },

  listAuditLogs(params = {}) {
    return listAuditLogs(params);
  },

  listEscalations(params = {}) {
    return listEscalations(params);
  },

  scanEscalations(payload = {}) {
    return scanEscalations(payload);
  },

  resolveEscalation(escalationId) {
    return resolveEscalation(escalationId);
  },

  retryEscalation(escalationId, payload = {}) {
    return retryEscalation(escalationId, payload);
  },

  bulkConversationAction(payload = {}) {
    return bulkConversationAction(payload);
  },

  getContactTimeline(contactId) {
    return getContactTimeline(contactId);
  },

  setHotClientStatus(contactId, payload) {
    return setHotClientStatus(contactId, payload);
  },

  analyzeConversation(conversationId) {
    return analyzeConversation(conversationId);
  },
};

export default omnichannelInboxApi;
